import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

export type FontFamily = 'default' | 'open-sans' | 'atkinson' | 'lexend' | 'verdana' | 'opendyslexic';

export interface AccessibilityPrefs {
  /** Base font-size multiplier: 1 = 100 %, 0.875 = 87.5 %, 1.25 = 125 %, etc. */
  fontScale: number;
  /** Font family override. */
  fontFamily: FontFamily;
  /** Increase line-height for readability. */
  lineHeightLoose: boolean;
  /** Increase letter-spacing. */
  wideLetterSpacing: boolean;
  /** Underline all links so they're distinguishable without colour alone. */
  underlineLinks: boolean;
  /** Highlight focused elements with extra emphasis. */
  highlightFocus: boolean;
  /** Reduce motion across the app. */
  reduceMotion: boolean;
}

interface AccessibilityPrefsState extends AccessibilityPrefs {
  setFontScale: (scale: number) => void;
  setFontFamily: (family: FontFamily) => void;
  setLineHeightLoose: (v: boolean) => void;
  setWideLetterSpacing: (v: boolean) => void;
  setUnderlineLinks: (v: boolean) => void;
  setHighlightFocus: (v: boolean) => void;
  setReduceMotion: (v: boolean) => void;
  resetAll: () => void;
}

const DEFAULTS: AccessibilityPrefs = {
  fontScale: 1,
  fontFamily: 'default',
  lineHeightLoose: false,
  wideLetterSpacing: false,
  underlineLinks: false,
  highlightFocus: false,
  reduceMotion: false,
};

export const FONT_SCALE_OPTIONS = [
  { label: '85%', value: 0.85 },
  { label: '100%', value: 1 },
  { label: '115%', value: 1.15 },
  { label: '125%', value: 1.25 },
] as const;

export const FONT_FAMILY_OPTIONS: { label: string; value: FontFamily; css: string }[] = [
  { label: 'Default', value: 'default', css: '"Roboto", "Helvetica", "Arial", sans-serif' },
  { label: 'Open Sans', value: 'open-sans', css: '"Open Sans", "Roboto", sans-serif' },
  { label: 'Atkinson Hyperlegible', value: 'atkinson', css: '"Atkinson Hyperlegible", "Roboto", sans-serif' },
  { label: 'Lexend', value: 'lexend', css: '"Lexend", "Roboto", sans-serif' },
  { label: 'Verdana', value: 'verdana', css: '"Verdana", "Geneva", sans-serif' },
  { label: 'OpenDyslexic', value: 'opendyslexic', css: '"OpenDyslexic", "Roboto", sans-serif' },
];

export const useAccessibilityPrefsStore = create<AccessibilityPrefsState>()(
  persist(
    (set) => ({
      ...DEFAULTS,
      setFontScale: (fontScale) => set({ fontScale }),
      setFontFamily: (fontFamily) => set({ fontFamily }),
      setLineHeightLoose: (lineHeightLoose) => set({ lineHeightLoose }),
      setWideLetterSpacing: (wideLetterSpacing) => set({ wideLetterSpacing }),
      setUnderlineLinks: (underlineLinks) => set({ underlineLinks }),
      setHighlightFocus: (highlightFocus) => set({ highlightFocus }),
      setReduceMotion: (reduceMotion) => set({ reduceMotion }),
      resetAll: () => set(DEFAULTS),
    }),
    {
      name: 'equip-accessibility-prefs',
      storage: createJSONStorage(() => localStorage),
    },
  ),
);
